// Şub 27, 2026 — DPD Otomatik İçe Aktarım Ayarları
// SUPER_ADMIN her gece 00:10'da (veya seçtiği saatte) otomatik ingest'i
// açar/kapatır. Son çalıştırma sonucunu ve manual "Şimdi Çalıştır" butonu
// da içerir. Diyalog trigger'ı File Categories sayfasından açılır.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle, DialogTrigger,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Clock, PlayCircle, Settings2, RefreshCw, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function DPDAutoIngestSettings() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [settings, setSettings] = useState(null);
  const [hour, setHour] = useState(0);
  const [minute, setMinute] = useState(10);
  const [enabled, setEnabled] = useState(true);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dpd/auto-ingest/settings');
      const d = r.data?.data;
      setSettings(d);
      setEnabled(!!d?.enabled);
      setHour(d?.cron_hour ?? 0);
      setMinute(d?.cron_minute ?? 10);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchSettings();
  }, [open, fetchSettings]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/dpd/auto-ingest/settings', {
        enabled, cron_hour: hour, cron_minute: minute,
      });
      const d = r.data?.data;
      setSettings(d);
      toast.success(`Kaydedildi: ${d.enabled ? `${String(d.cron_hour).padStart(2, '0')}:${String(d.cron_minute).padStart(2, '0')} aktif` : 'kapalı'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm('Otomatik ingest hemen çalıştırılacak (tüm firmalar). Devam?')) return;
    setRunning(true);
    try {
      const r = await api.post('/dpd/auto-ingest/run-now');
      const d = r.data?.data;
      const lr = d?.last_result;
      if (lr) {
        toast.success(`${lr.total_ingested} dosya aktarıldı, ${lr.total_errors} hata, ${lr.total_skipped_earlier} eski sürüm atlandı`);
      } else {
        toast.success('Çalıştırıldı');
      }
      setSettings(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Çalıştırılamadı');
    } finally {
      setRunning(false);
    }
  };

  const lastResult = settings?.last_result;
  const lastRunAt = settings?.last_run_at ? new Date(settings.last_run_at).toLocaleString('tr-TR') : null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-9" data-testid="dpd-auto-ingest-trigger">
          <Settings2 className="h-3.5 w-3.5 mr-1.5" />
          Otomatik Aktarım
          {settings?.enabled && (
            <Badge variant="outline" className="ml-1.5 text-[9px] py-0 px-1 border-emerald-300 text-emerald-700">
              {String(settings.cron_hour).padStart(2, '0')}:{String(settings.cron_minute).padStart(2, '0')}
            </Badge>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="dpd-auto-ingest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-600" />
            DPD Otomatik İçe Aktarım
          </DialogTitle>
          <DialogDescription>
            Her gece belirlediğin saatte tüm firmalar için mail arşivindeki
            yeni DPD dosyaları otomatik DB'ye yazılır (sabah 10:00 sürümleri
            atlanır, sadece akşam nihai versiyonu alınır).
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />Yükleniyor...
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between p-3 rounded-lg border bg-slate-50 dark:bg-slate-900/40">
              <div>
                <Label htmlFor="auto-ingest-toggle" className="text-sm font-semibold">Cron Etkin</Label>
                <p className="text-[11px] text-gray-500 mt-0.5">Kapatırsan mail arşivi dolar ama DB'ye yazılmaz — Kategoriler sayfasından manuel aktarırsın.</p>
              </div>
              <Switch
                id="auto-ingest-toggle"
                checked={enabled}
                onCheckedChange={setEnabled}
                data-testid="dpd-auto-ingest-toggle"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="auto-ingest-hour" className="text-xs">Saat (0-23)</Label>
                <Input
                  id="auto-ingest-hour"
                  type="number"
                  min={0} max={23}
                  value={hour}
                  onChange={(e) => setHour(Math.max(0, Math.min(23, parseInt(e.target.value || '0', 10))))}
                  disabled={!enabled}
                  data-testid="dpd-auto-ingest-hour"
                />
              </div>
              <div>
                <Label htmlFor="auto-ingest-minute" className="text-xs">Dakika (0-59)</Label>
                <Input
                  id="auto-ingest-minute"
                  type="number"
                  min={0} max={59}
                  value={minute}
                  onChange={(e) => setMinute(Math.max(0, Math.min(59, parseInt(e.target.value || '0', 10))))}
                  disabled={!enabled}
                  data-testid="dpd-auto-ingest-minute"
                />
              </div>
            </div>
            <p className="text-[11px] text-gray-500 -mt-2">
              Şu an ayar: <b className="font-mono">{String(hour).padStart(2, '0')}:{String(minute).padStart(2, '0')}</b> — Belçika saati (Europe/Brussels)
            </p>

            {/* Son çalıştırma bilgisi */}
            <div className="p-3 rounded-lg border bg-white dark:bg-slate-950">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold">Son Çalıştırma</span>
                {lastRunAt && (
                  <span className="text-[10px] text-gray-500">{lastRunAt}</span>
                )}
              </div>
              {lastResult ? (
                <div className="text-[11px] space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                    <span><b>{lastResult.total_ingested}</b> dosya aktarıldı</span>
                    <span className="text-gray-500">·</span>
                    <span><b>{lastResult.total_skipped_earlier}</b> eski sürüm atlandı</span>
                    {lastResult.total_errors > 0 && (
                      <>
                        <span className="text-gray-500">·</span>
                        <span className="text-red-600 flex items-center gap-0.5">
                          <AlertCircle className="h-3 w-3" /><b>{lastResult.total_errors}</b> hata
                        </span>
                      </>
                    )}
                  </div>
                  <div className="text-gray-500">
                    {lastResult.per_firm?.length || 0} firma tarandı
                  </div>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">Henüz otomatik çalışma yapılmadı.</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2 flex-col-reverse sm:flex-row">
          <Button
            variant="outline"
            onClick={runNow}
            disabled={running || loading}
            data-testid="dpd-auto-ingest-run-now"
            className="sm:mr-auto"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
            Şimdi Çalıştır (Test)
          </Button>
          <Button variant="outline" onClick={fetchSettings} disabled={loading} data-testid="dpd-auto-ingest-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Yenile
          </Button>
          <Button onClick={save} disabled={saving || loading} data-testid="dpd-auto-ingest-save">
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
